/**
 * The app never shows a raw `true` / `false` to a user. Every surface that
 * renders a boolean model field — table cells, the cards-view row summary, the
 * record inspector, the filter builder's value picker — reads "Yes" / "No"
 * instead, and they all go through here so the wording stays in one place.
 */
export const BOOLEAN_YES_LABEL = 'Yes';
export const BOOLEAN_NO_LABEL = 'No';

/**
 * A nullable boolean is a THREE-state field: yes, no, and "no value recorded".
 * The third state renders as an em dash rather than a blank, because a blank
 * cell in a Yes/No column reads as "No" at a glance and quietly misinforms.
 *
 * This is the Playlists report's long-standing convention (its `formatBool`
 * predates this module); every other surface now matches it.
 */
export const BOOLEAN_EMPTY_LABEL = '—';

/** "Yes" / "No" for a real boolean. */
export function booleanDisplayString(value: boolean): string {
    return value ? BOOLEAN_YES_LABEL : BOOLEAN_NO_LABEL;
}

/**
 * "Yes" / "No" for a value that *should* be a boolean but reaches the frontend
 * in a looser shape: Elasticsearch hands some boolean columns back as 0/1, and
 * MySQL tinyint columns can arrive as the strings "0"/"1" or "true"/"false".
 *
 * Returns null for anything not recognisably boolean (including null itself)
 * so callers can fall back to their own formatting rather than printing "No"
 * for a missing value.
 */
export function booleanDisplayStringOrNull(value: unknown): string | null {
    if (typeof value === 'boolean') {
        return booleanDisplayString(value);
    }
    if (value === 1 || value === 0) {
        return booleanDisplayString(value === 1);
    }
    if (value === 'true' || value === 'false') {
        return booleanDisplayString(value === 'true');
    }
    if (value === '1' || value === '0') {
        return booleanDisplayString(value === '1');
    }
    return null;
}

/**
 * The full three-state rendering for a boolean model field: "Yes", "No", or an
 * em dash when no value is recorded. Use this wherever a boolean COLUMN is
 * rendered — the column's declared type is what says the blank means "no value
 * recorded" rather than "not a boolean".
 */
export function booleanCellDisplayString(value: unknown): string {
    return booleanDisplayStringOrNull(value) ?? BOOLEAN_EMPTY_LABEL;
}
